import Phaser from "phaser";
import { BigNumber } from "@yjsgames/bignumber";
import { flyToTarget, spark, explosionRing, chainBeam } from "@yjsgames/phaser-fx";
import { BRICK_COMBO, MODULE_BRICK_BALANCE, MYSTERY_BRICK, SHIELD_BRICK } from "../data/balance.js";
import { nextComboCount, comboPitchMultiplier } from "../objects/brickCombo.js";
import { Brick } from "../objects/Brick.js";
import { Projectile, type NearestBrickLocator } from "../objects/Projectile.js";
import { reboundDirection } from "../objects/bounceBounds.js";
import { emitEnemyShipDestructionFx } from "../objects/enemyShipDestructionFx.js";
import { emitEnemyShipHitFx } from "../objects/enemyShipHitFx.js";
import { hexToTint } from "../objects/brickAppearance.js";
import { resolveBrickDamage } from "../objects/brickDamage.js";
import { rollExecute, type ProjectileSpec } from "../models/ProjectileSpec.js";
import type { GameContext } from "../app/GameContext.js";
import { CombatSystem } from "../systems/CombatSystem.js";
import type { MelodySequencer } from "../systems/MelodySequencer.js";
import { resolveMysteryRewards, mysteryBurstCount } from "../systems/rollMysteryReward.js";
import { spreadAngleOffsets } from "../systems/spreadAngleOffsets.js";
import { selectExplosionTargets, selectChainTargets } from "./battlefieldAreaEffects.js";
import type { BattlefieldRuntimePools } from "./battlefieldRuntimePools.js";
import { sceneLauncherY } from "../ui/sceneLayout.js";
import { PALETTE } from "../themes/palette.js";
import { FONT_FAMILY, TYPO } from "../ui/uiTokens.js";
import { formatBig } from "../ui/formatNumber.js";
import { diag } from "../app/diag.js";
import type { RectMask } from "@yjsgames/phaser-ui";
import { normalizeGoldGainCheatMultiplier } from "../systems/CheatActions.js";
import { DEPTH } from "../objects/depthLayers.js";
import { resolveSplitSpawnBudget } from "./battlefieldSplitBudget.js";
import { emitCombatGoldGainFx } from "./battlefieldCombatGoldFx.js";

const specialLog = diag("specialBrick");
const COMBAT_DAMAGE_TEXT_ENABLED = false;
export interface BattlefieldImpactRuntimeDeps {
  readonly scene: Phaser.Scene;
  readonly ctx: GameContext;
  readonly combat: CombatSystem;
  readonly runtime: () => BattlefieldRuntimePools;
  readonly nearestBrick: NearestBrickLocator;
  readonly playfieldBounds: () => ReturnType<typeof import("../ui/sceneLayout.js").scenePlayfieldBounds>;
  readonly melodySequencer: MelodySequencer;
  readonly onFieldCleared: () => void;
  readonly persistNow: () => void;
  readonly rewardTargetPos: (kind: "gold" | "diamond" | "artifact") => { x: number; y: number; iconKey: string } | null;
  readonly forceMysteryBricks: () => boolean;
  readonly gameviewMask: () => RectMask | undefined;
  readonly goldGainMultiplier?: () => number;
}

export class BattlefieldImpactRuntime {
  constructor(private readonly deps: BattlefieldImpactRuntimeDeps) {}
  readonly handleHit: Phaser.Types.Physics.Arcade.ArcadePhysicsCallback =
    (projObj, brickObj): void => {
      const proj = projObj as unknown as Projectile;
      const brick = brickObj as unknown as Brick;
      if (!proj.active || !brick.active) return;
      if (proj.hitSet.has(brick)) return;
      proj.hitSet.add(brick);
      const vel = proj.currentVelocity();
      const dir = reboundDirection(vel.x, vel.y, proj.x, proj.y, brick.x, brick.y);
      const textDrift = { x: dir.x * 18, y: dir.y * 18 };
      if (proj.isCrit && this.runtime.allowTextFx()) {
        this.emitCombatTextFx({
          x: proj.x,
          y: proj.y,
          text: MODULE_BRICK_BALANCE.critText,
          color: MODULE_BRICK_BALANCE.critTextColor,
          driftX: textDrift.x,
          driftY: textDrift.y,
        });
      }

      const shakeScale = proj.isCrit
        ? MODULE_BRICK_BALANCE.impactShakeCritScale
        : MODULE_BRICK_BALANCE.impactShakeScale;
      const damageRatio = brick.hp.lte(BigNumber.ZERO)
        ? 1
        : proj.damage.gte(brick.hp)
          ? 1
          : Math.max(0, proj.damage.div(brick.hp).toNumber());
      if (this.runtime.allowImpactFx()) {
        brick.playImpactShake(dir.x, dir.y, shakeScale);
        this.runtime.maskFx(
          emitEnemyShipHitFx(this.scene, this.runtime.fxImagePool, {
            projectileX: proj.x,
            projectileY: proj.y,
            brickX: brick.x,
            brickY: brick.y,
            brickWidth: brick.displayWidth,
            brickHeight: brick.displayHeight,
            tint: brick.tintTopLeft,
            isCrit: proj.isCrit,
            damageRatio,
          }),
        );
      }
      this.damageBrick(brick, proj.damage, proj.spec.goldMultiplier, proj.spec, proj.isCrit, textDrift);
      if (proj.spec.shockwaveRadius > 0 && proj.spec.shockwaveDamageRatio > 0) {
        this.applyShockwave(
          brick.x,
          brick.y,
          proj.spec.shockwaveRadius,
          proj.damage.mul(BigNumber.fromNumber(proj.spec.shockwaveDamageRatio)).max(BigNumber.ONE),
          brick,
          proj.isCrit,
        );
      }
      if (proj.spec.explosionRadius > 0) {
        this.deps.ctx.soundController().play("explosion");
        if (this.runtime.allowImpactFx()) this.runtime.maskFx(
          explosionRing(
            this.scene,
            brick.x,
            brick.y,
            proj.spec.explosionRadius,
            proj.isCrit ? { color: hexToTint(MODULE_BRICK_BALANCE.critExplosionColor) } : undefined,
          ),
        );
        this.applyExplosion(
          brick.x,
          brick.y,
          proj.spec.explosionRadius,
          proj.damage.mul(BigNumber.fromNumber(0.6)).max(BigNumber.ONE),
          brick,
        );
      }
      if (proj.spec.chainCount > 0) {
        this.deps.ctx.soundController().play("chain");
        this.applyChain(
          brick,
          proj.spec.chainCount,
          proj.damage.mul(BigNumber.fromNumber(0.5)).max(BigNumber.ONE),
          proj.spec.goldMultiplier,
        );
      }
      if (brick.active && proj.spec.aftershockDamageRatio > 0) {
        this.scheduleAftershock(
          brick,
          proj.damage.mul(BigNumber.fromNumber(proj.spec.aftershockDamageRatio)).max(BigNumber.ONE),
          proj.spec.aftershockDelayMs,
          proj.spec.goldMultiplier,
          proj.isCrit,
        );
      }
      if (proj.spec.splitCount > 0 && !proj.splitBounceUsed) {
        this.spawnSplit(brick.x, brick.y, proj.spec);
        proj.reflectOffBrick(brick.x, brick.y, brick.displayWidth / 2, brick.displayHeight / 2);
        proj.splitBounceUsed = true;
        return;
      }
      if (proj.pierceLeft > 0) {
        proj.pierceLeft -= 1;
      } else {
        this.fadeProjectileAfterImpact(proj, dir);
      }
    };

  private get scene(): Phaser.Scene {
    return this.deps.scene;
  }
  private get runtime(): BattlefieldRuntimePools {
    return this.deps.runtime();
  }
  private fadeProjectileAfterImpact(
    proj: Projectile,
    rebound: { x: number; y: number },
  ): void {
    if (!proj.active) return;
    if (!this.runtime.allowImpactFx()) {
      this.runtime.releaseProjectile(proj);
      return;
    }
    const body = proj.body as Phaser.Physics.Arcade.Body | null;
    if (body) {
      body.stop();
      body.enable = false;
    }
    proj.setActive(false);
    this.scene.tweens.add({
      targets: proj,
      x: proj.x + rebound.x * 12,
      y: proj.y + rebound.y * 12,
      alpha: 0,
      scale: 0.75,
      duration: 90,
      ease: "Quad.easeOut",
      onComplete: () => this.runtime.releaseProjectile(proj),
    });
  }

  private damageBrick(
    brick: Brick,
    dmg: BigNumber,
    goldMultiplier = 1,
    spec?: ProjectileSpec,
    isCrit = false,
    textDrift?: { readonly x: number; readonly y: number },
  ): boolean {
    if (!brick.active) return false;
    const res = resolveBrickDamage(
      { kind: brick.kind, shieldHits: brick.shieldHits, armor: brick.armor },
      dmg,
    );
    if (res.blockedByShield) {
      brick.consumeShield();
      if (COMBAT_DAMAGE_TEXT_ENABLED && this.runtime.allowTextFx()) {
        this.emitCombatTextFx({
          x: brick.x,
          y: brick.y - 6,
          text: "-1",
          color: SHIELD_BRICK.tint,
          driftX: textDrift?.x,
          driftY: textDrift?.y,
        });
      }
      specialLog.diag("shield", `block->hits=${brick.shieldHits} @${brick.gridX},${brick.gridY}`);
      return false;
    }
    dmg = res.finalDamage;
    if (brick.kind === "armor") {
      specialLog.diag(
        "armor",
        `final=${formatBig(res.finalDamage)} armor=${formatBig(brick.armor)} @${brick.gridX},${brick.gridY}`,
      );
    }
    const markMul = brick.markDamageMultiplier;
    if (markMul > 1) {
      dmg = dmg.mul(BigNumber.fromNumber(markMul));
      brick.consumeMarkHit();
    }
    if (spec) this.tryApplyMark(brick, spec, textDrift);
    if (spec && rollExecute(brick.hp, brick.maxHp, spec.executeThreshold, spec.executeChance, Math.random)) {
      dmg = brick.hp;
    }
    if (COMBAT_DAMAGE_TEXT_ENABLED && this.runtime.allowTextFx()) {
      this.emitCombatTextFx({
        x: brick.x,
        y: brick.y - 6,
        text: formatBig(dmg),
        color: isCrit ? MODULE_BRICK_BALANCE.critTextColor : PALETTE.damageText,
        fontSize: isCrit ? MODULE_BRICK_BALANCE.critDamageTextSize : undefined,
        driftX: textDrift?.x,
        driftY: textDrift?.y,
      });
    }
    const goldMult = this.deps.combat.goldMultiplier(goldMultiplier);
    const hitGoldGain = this.applyGoldGainMultiplier(dmg.mul(goldMult));
    this.deps.ctx.economy().addGold(hitGoldGain);
    const destroyed = brick.takeDamage(dmg);
    if (spec) {
      const mastery = this.deps.ctx.moduleMastery();
      for (const id of spec.sourceModuleIds) {
        mastery.addHit(id);
        if (destroyed) mastery.addKill(id);
      }
    }
    const sound = this.deps.ctx.soundController();
    const nowMs = this.scene.time.now;
    brick.comboCount = nextComboCount(brick.comboCount, brick.lastHitMs, nowMs, BRICK_COMBO.windowMs);
    brick.lastHitMs = nowMs;
    const comboPitch = comboPitchMultiplier(
      brick.comboCount,
      BRICK_COMBO.semitoneStep,
      BRICK_COMBO.maxSemitone,
    );
    if (!destroyed) {
      sound.playBrickNote(brick.strength, comboPitch);
      return false;
    }
    const melodyNote = this.deps.melodySequencer.isEmpty ? null : this.deps.melodySequencer.nextNote();
    if (melodyNote) sound.playMelodyNote(melodyNote);
    else sound.playBrickNote(brick.strength, comboPitch);
    if (brick.kind === "mystery") {
      emitCombatGoldGainFx(this.scene, this.runtime, brick, hitGoldGain);
      this.grantMysteryReward(brick, goldMult);
    } else {
      const destroyGoldGain = this.applyGoldGainMultiplier(brick.rewardGold.mul(goldMult));
      this.deps.ctx.economy().addGold(destroyGoldGain);
      emitCombatGoldGainFx(this.scene, this.runtime, brick, hitGoldGain.add(destroyGoldGain));
    }
    if (this.runtime.allowDestroyFx()) {
      this.runtime.maskFx(
        emitEnemyShipDestructionFx(this.scene, this.runtime.fxImagePool, {
          x: brick.x,
          y: brick.y,
          displayWidth: brick.displayWidth,
          displayHeight: brick.displayHeight,
          tint: brick.tintTopLeft,
          isCrit,
        }),
      );
    }
    this.runtime.brickPool.release(brick);
    if (this.runtime.bricks.countActive(true) === 0) this.deps.onFieldCleared();
    return true;
  }

  private tryApplyMark(
    brick: Brick,
    spec: ProjectileSpec,
    textDrift?: { readonly x: number; readonly y: number },
  ): void {
    if (spec.markChance <= 0 || spec.markDamageMultiplier <= 1 || spec.markHits <= 0) return;
    if (Math.random() >= spec.markChance) return;
    brick.applyMarkStatus(spec.markDamageMultiplier, spec.markHits);
    if (this.runtime.allowTextFx()) {
      this.emitCombatTextFx({
        x: brick.x,
        y: brick.y - brick.displayHeight / 2 - 8,
        text: "MARK",
        color: PALETTE.signalDanger,
        fontSize: TYPO.tiny,
        riseBy: 12,
        duration: 320,
        driftX: textDrift?.x,
        driftY: textDrift?.y,
      });
    }
  }

  private emitCombatTextFx(opts: {
    readonly x: number;
    readonly y: number;
    readonly text: string;
    readonly color: string;
    readonly fontSize?: string;
    readonly riseBy?: number;
    readonly duration?: number;
    readonly driftX?: number;
    readonly driftY?: number;
  }): void {
    const riseBy = opts.riseBy ?? 20;
    const duration = opts.duration ?? 130;
    const driftX = opts.driftX ?? 0;
    const driftY = opts.driftY ?? -riseBy;
    const label = this.runtime.fxTextPool.acquire();
    this.scene.tweens.killTweensOf(label);
    label
      .setText(opts.text)
      .setStyle({
        fontFamily: FONT_FAMILY,
        fontSize: opts.fontSize ?? "9px",
        color: opts.color,
      })
      .setPosition(opts.x, opts.y)
      .setOrigin(0.5)
      .setDepth(DEPTH.combatTextFx)
      .setAlpha(opts.fontSize ? 0.2 : 0.12)
      .setScale(1)
      .setActive(true)
      .setVisible(true);
    this.runtime.maskFx(label);
    this.scene.tweens.add({
      targets: label,
      x: opts.x + driftX,
      y: opts.y + driftY,
      alpha: 0,
      duration,
      ease: "Quad.easeOut",
      onComplete: () => this.runtime.fxTextPool.release(label),
    });
  }

  private scheduleAftershock(
    brick: Brick,
    dmg: BigNumber,
    delayMs: number,
    goldMultiplier: number,
    isCrit: boolean,
  ): void {
    const delay = Math.max(30, delayMs || 120);
    this.scene.time.delayedCall(delay, () => {
      if (!brick.active) return;
      this.deps.ctx.soundController().play("aftershock");
      if (this.runtime.allowImpactFx()) this.runtime.maskFx(
        explosionRing(this.scene, brick.x, brick.y, Math.max(brick.displayWidth, brick.displayHeight) * 0.55, {
          color: hexToTint(PALETTE.accentAmber),
          alpha: 0.5,
          endScale: 1.5,
          duration: 220,
        }),
      );
      if (this.runtime.allowImpactFx()) this.runtime.maskFx(
        spark(this.scene, brick.x, brick.y, "hit_spark_white", {
          startScale: 0.55,
          endScale: 1.35,
          tint: hexToTint(PALETTE.accentAmber),
          duration: 180,
        }),
      );
      this.damageBrick(brick, dmg, goldMultiplier, undefined, isCrit);
    });
  }

  private grantMysteryReward(brick: Brick, goldMult: BigNumber): void {
    const economy = this.deps.ctx.economy();
    const mystery = this.deps.ctx.mysteryBlock();
    const { rewardKind, goldMultiplier, tryDiamond, diamondAmount, tryRelic, relicId } = resolveMysteryRewards(
      Math.random,
      {
        goldMultipliers: MYSTERY_BRICK.goldMultipliers,
        diamondTiers: MYSTERY_BRICK.diamondTiers,
        diamondChance: MYSTERY_BRICK.diamondChance,
        relicChance: MYSTERY_BRICK.relicChance,
      },
      {
        canGrantDiamond: mystery.canGrantDiamond(),
        canGrantRelic: mystery.canGrantRelic(),
        force: this.deps.forceMysteryBricks(),
      },
    );
    const goldGranted = rewardKind === "gold";
    const multiplier = goldGranted ? goldMultiplier : 1;
    const goldGain = goldGranted
      ? this.applyGoldGainMultiplier(
        brick.rewardGold.mul(goldMult).mul(BigNumber.fromNumber(multiplier)),
      )
      : BigNumber.fromNumber(0);
    if (goldGranted) economy.addGold(goldGain);
    let diamondGranted = false;
    let relicGranted = false;
    if (tryDiamond) {
      economy.addDiamond(BigNumber.fromNumber(diamondAmount));
      mystery.markDiamondGranted();
      diamondGranted = true;
    }
    if (tryRelic && relicId && this.deps.ctx.relic().acquire(relicId)) {
      mystery.markRelicGranted();
      relicGranted = true;
    }
    const radius = Math.max(brick.displayWidth, brick.displayHeight) * 0.6;
    if (this.runtime.allowImpactFx()) this.runtime.maskFx(
      explosionRing(this.scene, brick.x, brick.y, radius, {
        color: hexToTint(MYSTERY_BRICK.tint),
        alpha: 0.55,
        endScale: 2,
        duration: 420,
      }),
    );
    if (this.runtime.allowImpactFx()) this.runtime.maskFx(
      spark(this.scene, brick.x, brick.y, "hit_spark_white", {
        startScale: 0.8,
        endScale: 2,
        duration: 300,
      }),
    );
    this.flyMysteryRewards(brick, goldGranted, goldGain, multiplier, diamondGranted, diamondAmount, relicGranted);
    if (diamondGranted || relicGranted) this.deps.persistNow();
  }

  private flyMysteryRewards(
    brick: Brick,
    goldGranted: boolean,
    goldGain: BigNumber,
    multiplier: number,
    diamondGranted: boolean,
    diamondAmount: number,
    relicGranted: boolean,
  ): void {
    const goldTarget = this.deps.rewardTargetPos("gold");
    if (goldGranted && goldTarget) {
      flyToTarget(this.scene, {
        startX: brick.x,
        startY: brick.y,
        targetX: goldTarget.x,
        targetY: goldTarget.y,
        iconKey: goldTarget.iconKey,
        label: `+${formatBig(goldGain)}`,
        labelColor: MYSTERY_BRICK.toastColor,
        fontFamily: FONT_FAMILY,
        iconCount: mysteryBurstCount(multiplier),
      });
    }
    if (diamondGranted) {
      const dt = this.deps.rewardTargetPos("diamond");
      if (dt)
        flyToTarget(this.scene, {
          startX: brick.x,
          startY: brick.y,
          targetX: dt.x,
          targetY: dt.y,
          iconKey: dt.iconKey,
          label: `+${diamondAmount}`,
          labelColor: PALETTE.infoBlue,
          fontFamily: FONT_FAMILY,
          iconCount: mysteryBurstCount(diamondAmount),
        });
    }
    if (relicGranted) {
      const rt = this.deps.rewardTargetPos("artifact");
      if (rt)
        flyToTarget(this.scene, {
          startX: brick.x,
          startY: brick.y,
          targetX: rt.x,
          targetY: rt.y,
          iconKey: rt.iconKey,
          label: "유물!",
          labelColor: PALETTE.accentGold,
          fontFamily: FONT_FAMILY,
        });
    }
  }

  private applyGoldGainMultiplier(amount: BigNumber): BigNumber {
    const multiplier = normalizeGoldGainCheatMultiplier(this.deps.goldGainMultiplier?.() ?? 1);
    return multiplier === 1 ? amount : amount.mul(BigNumber.fromNumber(multiplier));
  }

  private applyExplosion(
    x: number,
    y: number,
    radius: number,
    dmg: BigNumber,
    exclude: Brick,
  ): void {
    const lx = this.scene.scale.width / 2;
    const ly = sceneLauncherY(this.scene);
    const targets = selectExplosionTargets(
      this.runtime.bricks.getChildren() as Brick[],
      x, y, radius, exclude, lx, ly,
    );
    for (const b of targets) this.damageBrick(b, dmg);
  }

  private applyShockwave(
    x: number,
    y: number,
    radius: number,
    dmg: BigNumber,
    exclude: Brick,
    isCrit: boolean,
  ): void {
    this.deps.ctx.soundController().play("shockwave");
    if (this.runtime.allowImpactFx()) this.runtime.maskFx(
      explosionRing(this.scene, x, y, radius, {
        color: hexToTint(PALETTE.infoBlue),
        alpha: isCrit ? 0.65 : 0.48,
        endScale: 1.75,
        duration: 300,
      }),
    );
    const targets = selectExplosionTargets(
      this.runtime.bricks.getChildren() as Brick[],
      x,
      y,
      radius,
      exclude,
      this.scene.scale.width / 2,
      sceneLauncherY(this.scene),
    );
    for (const b of targets) this.damageBrick(b, dmg);
  }

  private applyChain(from: Brick, count: number, dmg: BigNumber, goldMultiplier = 1): void {
    const targets = selectChainTargets(
      this.runtime.bricks.getChildren() as Brick[],
      from, from.x, from.y, count,
    );
    for (const t of targets) {
      if (this.runtime.allowImpactFx()) this.runtime.maskFx(chainBeam(this.scene, from.x, from.y, t.x, t.y));
      this.damageBrick(t, dmg, goldMultiplier);
    }
  }
  private spawnSplit(x: number, y: number, parent: ProjectileSpec): void {
    const planned = CombatSystem.splitChildCount(parent);
    const budget = resolveSplitSpawnBudget(planned, this.runtime.remainingProjectileSlots());
    const n = budget.count;
    if (n <= 0) return;
    const childSpec = CombatSystem.deriveSplitSpec(parent);
    childSpec.damageMultiplier *= budget.damageMultiplierScale;
    const speed = MODULE_BRICK_BALANCE.baseProjectileSpeed * 0.8;
    const offsets = spreadAngleOffsets(n);
    const bounds = this.deps.playfieldBounds();
    for (let i = 0; i < n; i++) {
      const angle = Phaser.Math.DegToRad(-90 + offsets[i] * 40);
      const p = this.runtime.projectilePool.acquire();
      p.configure(
        x,
        y,
        childSpec,
        this.deps.nearestBrick,
        bounds,
        this.runtime.emitGhost,
        this.runtime.releaseProjectile,
      );
      p.setScale(0.55);
      p.setContentMask(this.deps.gameviewMask()?.mask);
      p.launch(Math.cos(angle) * speed, Math.sin(angle) * speed);
    }
  }
}
